import React, { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { Search, X, Check } from 'lucide-react';
import { api } from '../../lib/api';

// "Preview as" school picker for TemplatePreviewPane (an ARIA 1.2 combobox
// with a list popup).
//
// 2026-09-28 — Husain: the old native <select> only held the campaign's
// eligible schools (or the first 50 contracted + maintained ones), so most
// schools could never be previewed. Now:
//   • focused with nothing typed, it offers the pane's own list — the sample
//     school first, then the suggestions the pane loaded;
//   • typing searches EVERY school through the same endpoint AccountPicker
//     uses: GET /api/admin/accounts?q=<term>. That search also matches the
//     principal's name and the city, so the answers are re-ranked here: a
//     match on the school's own name or DBN comes first.
// A school found by search is mapped with the pane's `toAccount`, so it is
// indistinguishable from one picked off the list.
//
// Keyboard: ↓/↑ move through the options (and open the list), Enter picks,
// Escape closes and puts the selected school's name back. The active option
// is announced through aria-activedescendant; focus never leaves the input.
//
// The pane also lives inside modals (template editor, campaign step, one-off
// email), so:
//   • Escape with the list open closes the LIST only. The event stops here;
//     Modal closes on a document-level Escape and would otherwise throw the
//     unsaved draft away.
//   • The list is as wide as the field (never wider than the space it has)
//     and sizes itself to the room inside whatever clips it — the modal's
//     scrolling body, or the window — opening upward when there is more room
//     above than below.
//   • Arrowing scrolls the list itself, never the page or the modal.

const SEARCH_DEBOUNCE_MS = 200;
const SEARCH_FETCH = 50; // asked of the server
const SEARCH_SHOWN = 20; // shown, best matches first
const LIST_MAX = 260;
const LIST_MIN = 96;
const POPUP_CHROME = 84; // the popup's heading, status line and the gaps around the list
const FLIP_BELOW = 200; // less room than this under the field: open upward when there is more above

// The top and bottom of what can actually be seen around `el`: the window,
// cut down by every ancestor that clips or scrolls.
function visibleBand(el) {
  let top = 0;
  let bottom = window.innerHeight || document.documentElement.clientHeight || 0;
  for (let p = el ? el.parentElement : null; p && p !== document.body; p = p.parentElement) {
    const oy = window.getComputedStyle(p).overflowY;
    if (oy === 'auto' || oy === 'scroll' || oy === 'hidden' || oy === 'clip') {
      const r = p.getBoundingClientRect();
      top = Math.max(top, r.top);
      bottom = Math.min(bottom, r.bottom);
    }
  }
  return { top, bottom };
}

// 0 = the school's name or DBN starts with the term, 1 = its name contains
// it, 2 = its DBN or slug contains it, 3 = matched on something else the
// server searches (principal, city).
function matchRank(a, term) {
  const t = term.toLowerCase();
  const name = String(a.name || '').toLowerCase();
  const dbn = String(a.dbn || '').toLowerCase();
  const slug = String(a.slug || '').toLowerCase();
  if (name.startsWith(t) || dbn.startsWith(t)) return 0;
  if (name.includes(t)) return 1;
  if (dbn.includes(t) || slug.includes(t)) return 2;
  return 3;
}
export function rankSchools(list, term) {
  return list
    .map((a, i) => ({ a, i, r: matchRank(a, term) }))
    .sort((x, y) => x.r - y.r || x.i - y.i)
    .slice(0, SEARCH_SHOWN)
    .map((x) => x.a);
}

export default function PreviewSchoolPicker({ label = 'Preview as', suggestions, selected, sampleId, onSelect, toAccount }) {
  const uid = useId();
  const inputId = `${uid}input`;
  const listId = `${uid}list`;
  const optionId = (i) => `${uid}opt${i}`;
  const labelFor = (a) => (a.id === sampleId ? `Sample · ${a.name}` : a.name);
  const selectedLabel = labelFor(selected);

  const [text, setText] = useState(selectedLabel);
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState(false);
  const [active, setActive] = useState(-1);
  const [results, setResults] = useState([]);
  const [status, setStatus] = useState('idle'); // idle | searching | done | error
  const [place, setPlace] = useState({ up: false, listMax: LIST_MAX });
  const wrapRef = useRef(null);
  const listRef = useRef(null);

  const term = typed ? text.trim() : '';
  const searching = term.length > 0;
  const options = !searching ? suggestions : (status === 'done' ? results : []);
  const expanded = open && options.length > 0;
  const activeId = expanded && active >= 0 && active < options.length ? optionId(active) : undefined;

  // While nobody is typing, the field names the selected school.
  useEffect(() => { if (!typed) setText(selectedLabel); }, [selectedLabel, typed]);

  // Debounced search across every school; a late answer for an older term is dropped.
  useEffect(() => {
    if (!open || !searching) { setStatus('idle'); return undefined; }
    let alive = true;
    setStatus('searching');
    const t = setTimeout(() => {
      Promise.resolve(api.get(`/api/admin/accounts?q=${encodeURIComponent(term)}&limit=${SEARCH_FETCH}`))
        .then((d) => {
          if (!alive) return;
          setResults(rankSchools(((d && d.accounts) || []).map(toAccount), term));
          setStatus('done');
          setActive(0);
        })
        .catch(() => { if (alive) { setResults([]); setStatus('error'); } });
    }, SEARCH_DEBOUNCE_MS);
    return () => { alive = false; clearTimeout(t); };
  }, [open, searching, term, toAccount]);

  // Keep the active option in view while arrowing through a long list. Only
  // the list scrolls: scrollIntoView would also move the modal and the page.
  useEffect(() => {
    const list = listRef.current;
    if (!activeId || !list) return;
    const el = document.getElementById(activeId);
    if (!el) return;
    const top = el.offsetTop; // the list is the options' offset parent
    if (top < list.scrollTop) list.scrollTop = top;
    else if (top + el.offsetHeight > list.scrollTop + list.clientHeight) list.scrollTop = top + el.offsetHeight - list.clientHeight;
  }, [activeId]);

  // Fit the list to the room it has, and open upward when that is the roomier side.
  useLayoutEffect(() => {
    if (!open) return undefined;
    const measure = () => {
      const wrap = wrapRef.current;
      if (!wrap) return;
      const box = wrap.getBoundingClientRect();
      const band = visibleBand(wrap);
      const below = band.bottom - box.bottom;
      const above = box.top - band.top;
      const up = below < FLIP_BELOW && above > below;
      const listMax = Math.max(LIST_MIN, Math.min(LIST_MAX, Math.floor((up ? above : below) - POPUP_CHROME)));
      setPlace((p) => (p.up === up && p.listMax === listMax ? p : { up, listMax }));
    };
    measure();
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
    };
  }, [open]);

  // Opens on the selected school (none active when it came from a search).
  const openList = () => {
    setOpen(true);
    setTyped(false);
    setActive(suggestions.findIndex((a) => a.id === selected.id));
  };
  const close = () => {
    setOpen(false);
    setTyped(false);
    setActive(-1);
    setText(selectedLabel);
  };
  const choose = (a) => {
    onSelect(a);
    setOpen(false);
    setTyped(false);
    setActive(-1);
    setText(labelFor(a));
  };
  const move = (delta) => {
    if (!open) { openList(); return; }
    if (!options.length) return;
    setActive((i) => {
      if (i < 0) return delta > 0 ? 0 : options.length - 1;
      return (i + delta + options.length) % options.length;
    });
  };

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); move(1); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); move(-1); }
    else if (e.key === 'Enter') {
      // Never submits a surrounding form: Enter here only ever picks a school.
      e.preventDefault();
      if (open && active >= 0 && options[active]) choose(options[active]);
    } else if (e.key === 'Escape') {
      // Closes the list and nothing else: a surrounding modal must not see it.
      if (open) { e.preventDefault(); e.stopPropagation(); close(); }
    }
  };

  const sample = suggestions.find((a) => a.id === sampleId) || null;
  // DBN, then the slug — unless the slug is just the DBN again (most schools).
  const metaFor = (a) => {
    if (a.id === sampleId) return `${a.name} · example values`;
    const slug = a.slug && String(a.slug).toLowerCase() !== String(a.dbn || '').toLowerCase() ? a.slug : '';
    return [a.dbn, slug].filter(Boolean).join(' · ');
  };

  let statusLine = null;
  let liveText = '';
  if (open && searching) {
    if (status === 'searching') { statusLine = 'Searching all schools…'; liveText = 'Searching…'; }
    else if (status === 'error') { statusLine = 'Couldn’t search right now. Try again.'; liveText = statusLine; }
    else if (status === 'done' && !results.length) { statusLine = `No school matches “${term}”.`; liveText = statusLine; }
    else if (status === 'done') liveText = `${results.length} school${results.length === 1 ? '' : 's'} found`;
  } else if (open) {
    statusLine = 'Type to search every school by name or DBN.';
  }

  return (
    <>
      <label htmlFor={inputId} className="text-[10px] uppercase tracking-wider text-gray-400">{label}</label>
      <div ref={wrapRef} className="relative flex-1 min-w-[220px] max-w-[380px]">
        <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-500 pointer-events-none" aria-hidden="true" />
        <input
          id={inputId}
          type="text"
          role="combobox"
          aria-expanded={expanded}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={activeId}
          autoComplete="off"
          spellCheck={false}
          value={text}
          placeholder="Search any school by name or DBN"
          onFocus={(e) => { openList(); e.target.select(); }}
          onClick={() => { if (!open) openList(); }}
          onChange={(e) => { setText(e.target.value); setTyped(true); setOpen(true); setActive(-1); }}
          onKeyDown={onKeyDown}
          onBlur={close}
          data-testid="preview-school-input"
          className={`w-full pl-7 ${selected.id !== sampleId ? 'pr-8' : 'pr-2'} py-1 rounded bg-white/[0.04] border border-white/[0.08] text-[12px] text-white placeholder-gray-500 truncate focus:outline-none focus:border-[#B0FC38]/60 focus:ring-2 focus:ring-[#B0FC38]/40`}
        />
        {selected.id !== sampleId && sample && (
          <button
            type="button"
            onClick={() => choose(sample)}
            aria-label="Preview as the sample school instead"
            title="Back to the sample school"
            data-testid="preview-school-clear"
            className="absolute right-1 top-1/2 -translate-y-1/2 inline-grid place-items-center w-6 h-6 rounded text-gray-400 hover:text-white hover:bg-white/[0.08] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#B0FC38]/60"
          >
            <X className="w-3.5 h-3.5" aria-hidden="true" />
          </button>
        )}
        {/* Mouse-down inside the popup must not blur the input (the blur closes it). */}
        {open && (
          <div
            className={`absolute left-0 right-0 z-50 rounded-xl border border-white/[0.1] bg-[#0f0f16] shadow-[0_20px_60px_-10px_rgba(0,0,0,0.8)] overflow-hidden ${place.up ? 'bottom-full mb-1' : 'top-full mt-1'}`}
            onMouseDown={(e) => e.preventDefault()}
            data-testid="preview-school-popup"
            data-placement={place.up ? 'above' : 'below'}
          >
            <div className="px-3 pt-2 pb-1 text-[10.5px] uppercase tracking-wider text-gray-400" aria-hidden="true">{searching ? 'All schools' : 'Suggested'}</div>
            {options.length > 0 && (
              <ul ref={listRef} id={listId} role="listbox" aria-label={searching ? 'Schools matching your search' : 'Suggested schools'} style={{ maxHeight: place.listMax }} className="relative overflow-y-auto list-none m-0 px-1 pb-1">
                {options.map((a, i) => {
                  const isActive = i === active;
                  const isSelected = a.id === selected.id;
                  return (
                    <li
                      key={a.id}
                      id={optionId(i)}
                      role="option"
                      aria-selected={isSelected}
                      onClick={() => choose(a)}
                      onMouseMove={() => { if (!isActive) setActive(i); }}
                      className={`flex items-center gap-2 px-2.5 py-1.5 rounded-md cursor-pointer ${isActive ? 'bg-white/[0.09] ring-1 ring-inset ring-[#B0FC38]/60' : ''}`}
                    >
                      <span className="min-w-0 flex-1">
                        <span className="block text-[12.5px] text-white truncate">{a.id === sampleId ? 'Sample school' : a.name}</span>
                        <span className="block text-[11px] text-gray-400 truncate">{metaFor(a)}</span>
                      </span>
                      {isSelected && <Check className="w-3.5 h-3.5 text-[#B0FC38] shrink-0" aria-hidden="true" />}
                    </li>
                  );
                })}
              </ul>
            )}
            {statusLine && <div className="px-3 py-2 border-t border-white/[0.06] text-[11.5px] text-gray-400">{statusLine}</div>}
          </div>
        )}
        <span className="sr-only" role="status" aria-live="polite">{liveText}</span>
      </div>
    </>
  );
}
